import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, CircularProgress, Alert, Grid } from '@mui/joy';
import { getAllEmployers } from '../../../apiconfig/api';
import EmployerCard from './components/EmployerCard';

const ApplyPage: React.FC = () => {
  const [employers, setEmployers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);

  const handleVideoVisibilityChange = (videoId: string, isVisible: boolean) => {
    if (isVisible) {
      setActiveVideoId(videoId);
    }
  };

  useEffect(() => {
    const fetchEmployers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all employers with active positions
        const response = await getAllEmployers({
          hasActivePositions: true,
          per_page: 100 // Get a large batch
        });
        
        setEmployers(response.data?.employers || []);
      } catch (err: any) {
        console.error('Error fetching employers:', err);
        setError(err.message || 'Failed to load employers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployers();
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f4f4f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f4f4f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}>
        <Container maxWidth="sm">
          <Alert color="danger">
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f4f4f4',
      py: 4
    }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            level="h1" 
            sx={{ 
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: '#1A1B1D',
              mb: 1
            }}
          >
            Find Your Next Opportunity
          </Typography>
          <Typography 
            level="body-lg" 
            sx={{ 
              color: '#7B7F85',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Browse employers hiring through GEEK and apply with video
          </Typography>
        </Box>

        {employers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography level="h3" sx={{ color: '#7B7F85', mb: 2 }}>
              No employers found
            </Typography>
            <Typography level="body-md" sx={{ color: '#9CA3AF' }}>
              Check back soon for new opportunities!
            </Typography>
          </Box>
        ) : (
          <Grid 
            container 
            spacing={3}
            sx={{
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
            }}
          >
            {employers.map((employer) => (
              <Grid key={employer.uuid} xs={12} sm={6} md={4} lg={4}>
                <EmployerCard 
                  employer={employer}
                  isActive={employer.introVideoUuid === activeVideoId}
                  playing={playing}
                  setPlaying={setPlaying}
                  muted={muted}
                  setMuted={setMuted}
                  onVisibilityChange={(isVisible: boolean) => 
                    handleVideoVisibilityChange(employer.introVideoUuid, isVisible)
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default ApplyPage;

